/**
 * Task calendar — a week view of everything that occupies time:
 *   • task deadlines (10-minute reminder slots; meetings their real length, click → task),
 *   • busy blocks you add here (one-off or weekly),
 *   • events synced read-only from external calendars (ICS address).
 * Plus the settings that drive availability: working hours/days and the
 * external calendar subscriptions.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Trash2, RefreshCw, Loader2, X, Clock, CalendarPlus, Link2, Info, Check, Copy, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import TaskDrawer from "@/components/TaskDrawer";

const HOUR_PX = 48;
const GRID_START = 7;   // 7 AM
const GRID_END = 21;    // 9 PM
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function mondayOf(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function minutesOf(d) { return d.getHours() * 60 + d.getMinutes(); }
function fmtHM(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(); d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function toLocalInput(d) {
  return `${ymd(d)}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function CalendarPage() {
  const confirm = useConfirm();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [work, setWork] = useState({ workHoursStart: "09:00", workHoursEnd: "18:00", workDays: [0, 1, 2, 3, 4, 5] });
  const [savingWork, setSavingWork] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [externals, setExternals] = useState([]);
  const [blockModal, setBlockModal] = useState(null);   // {block?} or {start, end} prefill
  const [showAddExternal, setShowAddExternal] = useState(false);
  const [externalForm, setExternalForm] = useState({ name: "", url: "", include_all_day: false });
  const [addingExternal, setAddingExternal] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [focusTask, setFocusTask] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [feed, setFeed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [feedBusy, setFeedBusy] = useState(false);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const todayStr = ymd(new Date());

  const loadAgenda = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/calendar/agenda", { params: { from: ymd(days[0]), to: ymd(days[6]) } });
      setItems(r.data.items || []);
      if (r.data.work_hours) {
        setWork({ workHoursStart: r.data.work_hours.workHoursStart, workHoursEnd: r.data.work_hours.workHoursEnd, workDays: r.data.work_hours.workDays });
      }
    } catch {
      toast.error("Couldn't load the calendar");
    } finally {
      setLoading(false);
    }
  }, [days]);

  const loadSide = useCallback(async () => {
    try {
      const [b, e] = await Promise.all([api.get("/calendar/blocks"), api.get("/calendar/external")]);
      setBlocks(b.data.blocks || []);
      setExternals(e.data.calendars || []);
    } catch { /* side panel is optional */ }
  }, []);

  useEffect(() => { loadAgenda(); }, [loadAgenda]);
  useEffect(() => { loadSide(); }, [loadSide]);
  useEffect(() => {
    api.get("/settings/assignees").then((r) => setAssignees(r.data.assignees || [])).catch(() => {});
    api.get("/calendar/feed").then((r) => setFeed(r.data)).catch(() => {});
  }, []);

  const copyFeed = async () => {
    try {
      await navigator.clipboard.writeText(feed.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — select the address and copy it manually");
    }
  };

  const rotateFeed = async () => {
    const ok = await confirm({
      title: "Get a new calendar address?",
      message: "The current address stops working immediately. You'll need to remove the old subscription from your calendar and add the new one.",
      confirmLabel: "Get a new address",
      tone: "danger",
    });
    if (!ok) return;
    setFeedBusy(true);
    try {
      const r = await api.post("/calendar/feed/rotate");
      setFeed((f) => ({ ...f, url: r.data.url }));
      toast.success("New address created — update your calendar subscription");
    } catch { toast.error("Couldn't create a new address"); } finally { setFeedBusy(false); }
  };

  const stopEmailInvites = async () => {
    setFeedBusy(true);
    try {
      await api.post("/settings/user", { calendarInvites: "never" });
      setFeed((f) => ({ ...f, email_invites: "never" }));
      toast.success("Email invites off — the subscription keeps your calendar up to date");
    } catch { toast.error("Couldn't change the setting"); } finally { setFeedBusy(false); }
  };

  // ── Task drawer (same one as the Tasks page) ──
  const openTask = async (id) => {
    try {
      const r = await api.get(`/tasks/${id}`);
      setFocusTask(r.data);
    } catch { toast.error("Couldn't open the task"); }
  };

  const updateFocusTask = async (patch) => {
    if (!focusTask) return;
    try {
      const r = await api.patch(`/tasks/${focusTask.id}`, patch);
      setFocusTask(r.data);
      toast.success("Saved");
      loadAgenda();
    } catch { toast.error("Save failed"); }
  };

  const deleteFocusTask = async () => {
    if (!focusTask) return;
    const ok = await confirm({ title: "Delete this task?", message: "The task and its history will be removed.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/tasks/${focusTask.id}`);
      setFocusTask(null);
      toast.success("Deleted");
      loadAgenda();
    } catch { toast.error("Delete failed"); }
  };

  const sendFocusInvite = async () => {
    if (!focusTask) return;
    setSendingInvite(true);
    try {
      const r = await api.post(`/tasks/${focusTask.id}/calendar-invite`);
      setFocusTask((f) => ({ ...f, calendar_invite: r.data.calendar_invite }));
      toast.success(`Calendar invite sent to ${(r.data.calendar_invite?.to || []).join(", ") || "your email"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send calendar invite");
    } finally { setSendingInvite(false); }
  };

  // ── Working hours ──
  const saveWork = async (patch) => {
    const next = { ...work, ...patch };
    setWork(next);
    setSavingWork(true);
    try {
      await api.post("/settings/user", next);
      toast.success("Working hours saved");
      loadAgenda();
    } catch {
      toast.error("Couldn't save working hours");
    } finally {
      setSavingWork(false);
    }
  };

  // ── Blocks ──
  const saveBlock = async (form) => {
    const body = form.kind === "once"
      ? { title: form.title, kind: "once", start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString() }
      : { title: form.title, kind: "weekly", days: form.days, start_time: form.start_time, end_time: form.end_time, until: form.until ? new Date(form.until + "T23:59").toISOString() : null };
    try {
      if (form.id) await api.put(`/calendar/blocks/${form.id}`, body);
      else await api.post("/calendar/blocks", body);
      toast.success(form.id ? "Block updated" : "Time blocked");
      setBlockModal(null);
      loadAgenda(); loadSide();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the block");
    }
  };

  const deleteBlock = async (id) => {
    const ok = await confirm({ title: "Remove this busy block?", message: "The time becomes available for task deadlines again.", confirmLabel: "Remove", tone: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/calendar/blocks/${id}`);
      setBlockModal(null);
      loadAgenda(); loadSide();
    } catch { toast.error("Couldn't remove the block"); }
  };

  // ── External calendars ──
  const addExternal = async (e) => {
    e.preventDefault();
    if (!externalForm.name.trim() || !externalForm.url.trim()) return;
    setAddingExternal(true);
    try {
      const r = await api.post("/calendar/external", externalForm);
      setExternals((cur) => [...cur, r.data]);
      setExternalForm({ name: "", url: "", include_all_day: false });
      setShowAddExternal(false);
      if (r.data.last_error) toast.warning(`Added, but the first sync failed: ${r.data.last_error}`);
      else toast.success(`Connected — ${r.data.event_count} upcoming event${r.data.event_count === 1 ? "" : "s"} found`);
      loadAgenda();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add the calendar");
    } finally {
      setAddingExternal(false);
    }
  };

  const syncExternal = async (id) => {
    setSyncingId(id);
    try {
      const r = await api.post(`/calendar/external/${id}/sync`);
      setExternals((cur) => cur.map((c) => (c.id === id ? r.data.calendar : c)));
      if (r.data.result?.ok) toast.success(`Synced — ${r.data.result.events} event${r.data.result.events === 1 ? "" : "s"}`);
      else toast.error(r.data.result?.error || "Sync failed");
      loadAgenda();
    } catch { toast.error("Sync failed"); } finally { setSyncingId(null); }
  };

  const toggleAllDay = async (cal) => {
    try {
      const r = await api.patch(`/calendar/external/${cal.id}`, { include_all_day: !cal.include_all_day });
      setExternals((cur) => cur.map((c) => (c.id === cal.id ? r.data : c)));
      loadAgenda();
    } catch { toast.error("Couldn't update the calendar"); }
  };

  const removeExternal = async (cal) => {
    const ok = await confirm({ title: `Disconnect “${cal.name}”?`, message: "Its events will no longer count as busy time.", confirmLabel: "Disconnect", tone: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/calendar/external/${cal.id}`);
      setExternals((cur) => cur.filter((c) => c.id !== cal.id));
      loadAgenda();
    } catch { toast.error("Couldn't disconnect"); }
  };

  // ── Grid helpers ──
  const itemsByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[ymd(d)] = []; });
    items.forEach((it) => {
      const s = new Date(it.start), e = new Date(it.end);
      // Split multi-day items per day column.
      let cur = new Date(s);
      while (cur < e) {
        const key = ymd(cur);
        const dayEnd = new Date(cur); dayEnd.setHours(23, 59, 59, 999);
        const segEnd = e < dayEnd ? e : dayEnd;
        if (map[key]) map[key].push({ ...it, segStart: new Date(cur), segEnd });
        cur = new Date(dayEnd.getTime() + 1);
      }
    });
    return map;
  }, [items, days]);

  const workStartMin = useMemo(() => { const [h, m] = work.workHoursStart.split(":").map(Number); return h * 60 + m; }, [work]);
  const workEndMin = useMemo(() => { const [h, m] = work.workHoursEnd.split(":").map(Number); return h * 60 + m; }, [work]);
  const gridHeight = (GRID_END - GRID_START) * HOUR_PX;

  const onGridClick = (day, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minutes = GRID_START * 60 + Math.floor((y / HOUR_PX) * 60 / 30) * 30;
    const start = new Date(day); start.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    const end = new Date(start.getTime() + 30 * 60000);
    setBlockModal({ start, end });
  };

  const weekLabel = `${days[0].toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">Calendar</h1>
          <p className="text-[14px] text-slate-500 mt-1">Where your task deadlines land, and the time that's already taken. Auto-deadlines and the deadline picker only suggest the gaps.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setBlockModal({ start: (() => { const d = new Date(); d.setMinutes(Math.ceil(d.getMinutes() / 30) * 30, 0, 0); return d; })(), end: null })}
            className="h-10 px-4 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-[13px] font-semibold transition active:scale-95" data-testid="block-time-btn">
            <Plus size={16} /> Block time
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        {/* Week grid */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="week-grid">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-1">
              <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="h-8 w-8 grid place-items-center rounded-lg border border-slate-200 hover:border-slate-400" aria-label="Previous week"><ChevronLeft size={15} /></button>
              <button onClick={() => setWeekStart(mondayOf(new Date()))} className="h-8 px-3 rounded-lg border border-slate-200 hover:border-slate-400 text-[12px] font-semibold text-slate-700">Today</button>
              <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="h-8 w-8 grid place-items-center rounded-lg border border-slate-200 hover:border-slate-400" aria-label="Next week"><ChevronRight size={15} /></button>
            </div>
            <div className="text-[13px] font-semibold text-slate-800">{weekLabel}</div>
            <div className="hidden md:flex items-center gap-3 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Task</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-500" /> Meeting</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-500" /> Blocked</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-violet-500" /> Other calendar</span>
              {loading && <Loader2 size={12} className="animate-spin" />}
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              {/* Day headers */}
              <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
                <div />
                {days.map((d, i) => {
                  const isToday = ymd(d) === todayStr;
                  const isWork = work.workDays.includes(i);
                  return (
                    <div key={i} className={"px-2 py-2 text-center border-l border-slate-100 " + (isWork ? "" : "bg-slate-50")}>
                      <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">{DAY_NAMES[i]}</div>
                      <div className={"text-[15px] font-bold leading-tight " + (isToday ? "text-white bg-blue-600 rounded-full w-7 h-7 grid place-items-center mx-auto" : "text-slate-800")}>{d.getDate()}</div>
                    </div>
                  );
                })}
              </div>
              {/* Body */}
              <div className="grid border-t border-slate-100" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
                <div className="relative" style={{ height: gridHeight }}>
                  {Array.from({ length: GRID_END - GRID_START }, (_, i) => (
                    <div key={i} className="absolute right-2 text-[10.5px] text-slate-400 -translate-y-1/2" style={{ top: i * HOUR_PX }}>
                      {i === 0 ? "" : fmtHM(`${String(GRID_START + i).padStart(2, "0")}:00`).replace(":00", "")}
                    </div>
                  ))}
                </div>
                {days.map((d, i) => {
                  const key = ymd(d);
                  const isWork = work.workDays.includes(i);
                  return (
                    <div key={key} className="relative border-l border-slate-100 cursor-crosshair" style={{ height: gridHeight }} onClick={(e) => onGridClick(d, e)} data-testid={`day-col-${key}`}>
                      {/* hour lines */}
                      {Array.from({ length: GRID_END - GRID_START }, (_, h) => (
                        <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: h * HOUR_PX }} />
                      ))}
                      {/* non-working shading */}
                      {!isWork ? (
                        <div className="absolute inset-0 bg-slate-50/80" />
                      ) : (
                        <>
                          <div className="absolute left-0 right-0 top-0 bg-slate-50/80" style={{ height: Math.max(0, (workStartMin - GRID_START * 60) / 60 * HOUR_PX) }} />
                          <div className="absolute left-0 right-0 bottom-0 bg-slate-50/80" style={{ height: Math.max(0, (GRID_END * 60 - workEndMin) / 60 * HOUR_PX) }} />
                        </>
                      )}
                      {/* now line */}
                      {key === todayStr && (() => {
                        const m = minutesOf(new Date()) - GRID_START * 60;
                        return m > 0 && m < (GRID_END - GRID_START) * 60 ? <div className="absolute left-0 right-0 border-t-2 border-red-400 z-10" style={{ top: m / 60 * HOUR_PX }} /> : null;
                      })()}
                      {/* items */}
                      {itemsByDay[key]?.map((it, idx) => {
                        const sMin = Math.max(GRID_START * 60, minutesOf(it.segStart));
                        const eMin = Math.min(GRID_END * 60, it.segEnd.getHours() === 23 && it.segEnd.getMinutes() === 59 ? GRID_END * 60 : minutesOf(it.segEnd));
                        if (eMin <= sMin) return null;
                        const top = (sMin - GRID_START * 60) / 60 * HOUR_PX;
                        const height = Math.max(18, (eMin - sMin) / 60 * HOUR_PX - 2);
                        const isTask = it.type === "task";
                        const isIcs = it.source === "ics";
                        const isMeeting = isTask && it.item_type === "meeting";
                        const cls = isMeeting
                          ? "bg-indigo-100 border-indigo-500 text-indigo-900 hover:bg-indigo-200"
                          : isTask
                            ? "bg-blue-50 border-blue-300 text-blue-900 hover:bg-blue-100"
                            : isIcs ? "bg-violet-50 border-violet-300 text-violet-900" : "bg-slate-100 border-slate-400 text-slate-800 hover:bg-slate-200";
                        return (
                          <button
                            key={(it.id || "x") + idx}
                            onClick={(e) => { e.stopPropagation(); if (isTask) openTask(it.id); else if (!isIcs) setBlockModal({ block: blocks.find((b) => b.id === it.id) || it }); }}
                            className={"absolute left-0.5 right-0.5 rounded-md border-l-[3px] border px-1.5 text-left overflow-hidden z-[5] " + cls}
                            style={{ top, height }}
                            title={`${it.title} · ${it.start_local}–${it.end_local}${isTask && it.assignee ? ` · ${it.assignee}` : ""}`}
                            data-testid={`cal-item-${it.id}`}
                          >
                            <div className="text-[11px] font-semibold leading-tight truncate">{isTask && it.task_no ? `#${it.task_no} ` : ""}{it.title}</div>
                            {height > 30 && <div className="text-[10px] opacity-70 truncate">{it.start_local}–{it.end_local}{isTask && it.deadline_source === "default" ? " · auto" : ""}</div>}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="px-4 py-2 text-[11.5px] text-slate-400 border-t border-slate-100">Click an empty spot to block that time. Grey areas are outside your working hours.</div>
        </section>

        {/* Side panel */}
        <aside className="space-y-4">
          {/* Subscribe */}
          <section className="bg-white border border-blue-200 rounded-xl p-4" data-testid="calendar-feed">
            <div className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.16em] text-blue-700 mb-2"><CalendarCheck size={13} /> Your tasks in your calendar</div>
            {!feed ? (
              <div className="text-[12px] text-slate-400">Loading…</div>
            ) : (
              <>
                <p className="text-[12px] text-slate-600 mb-3">
                  Add this private address to Google, Outlook or Apple Calendar once. All {feed.event_count} task{feed.event_count === 1 ? "" : "s"} appear straight away, and new ones show up on their own — no invites to accept.
                </p>
                <div className="flex gap-1.5 mb-2">
                  <input readOnly value={feed.url} onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 h-9 px-2.5 border border-slate-200 rounded-lg text-[11.5px] font-mono bg-slate-50 text-slate-600" data-testid="feed-url" />
                  <button onClick={copyFeed} className={"h-9 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 transition " + (copied ? "bg-emerald-600 text-white" : "bg-slate-900 text-white hover:bg-slate-700")} data-testid="feed-copy">
                    {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                  </button>
                </div>
                <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-2.5 leading-relaxed space-y-1">
                  <div><strong>Google Calendar:</strong> Other calendars → + → <em>From URL</em> → paste → Add. Google refreshes it every few hours.</div>
                  <div><strong>Outlook:</strong> Add calendar → Subscribe from web → paste.</div>
                  <div><strong>Apple / iPhone:</strong> Calendar → Add Account → Other → Add Subscribed Calendar → paste. Set refresh to hourly.</div>
                </div>
                {feed.email_invites !== "never" && (
                  <div className="mt-2 text-[11.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    Email invites are still on, so subscribed tasks would also arrive as invitations and show twice.
                    <button onClick={stopEmailInvites} disabled={feedBusy} className="ml-1 font-semibold underline underline-offset-2 disabled:opacity-50" data-testid="feed-stop-invites">Turn email invites off</button>
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
                  <span>Keep this address private — anyone with it can read your task titles.</span>
                  <button onClick={rotateFeed} disabled={feedBusy} className="shrink-0 ml-2 hover:text-red-600 underline underline-offset-2 disabled:opacity-50" data-testid="feed-rotate">New address</button>
                </div>
              </>
            )}
          </section>

          {/* Working hours */}
          <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="working-hours">
            <div className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-3"><Clock size={13} /> Working hours</div>
            <div className="flex items-center gap-2 text-[13px] text-slate-700">
              <input type="time" value={work.workHoursStart} step={900} onChange={(e) => saveWork({ workHoursStart: e.target.value })} disabled={savingWork} className="h-9 px-2 border border-slate-200 rounded-lg text-[13px] bg-white flex-1" data-testid="work-start" />
              <span className="text-slate-400">to</span>
              <input type="time" value={work.workHoursEnd} step={900} onChange={(e) => saveWork({ workHoursEnd: e.target.value })} disabled={savingWork} className="h-9 px-2 border border-slate-200 rounded-lg text-[13px] bg-white flex-1" data-testid="work-end" />
            </div>
            <div className="flex gap-1 mt-2.5">
              {DAY_NAMES.map((n, i) => {
                const on = work.workDays.includes(i);
                return (
                  <button key={n} onClick={() => saveWork({ workDays: on ? work.workDays.filter((d) => d !== i) : [...work.workDays, i].sort() })} disabled={savingWork}
                    className={"flex-1 h-8 rounded-lg border text-[11px] font-semibold transition " + (on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400")} data-testid={`work-day-${i}`}>
                    {n[0]}
                  </button>
                );
              })}
            </div>
            <p className="text-[11.5px] text-slate-400 mt-2">Auto-deadlines and suggested slots stay inside these hours.</p>
          </section>

          {/* External calendars */}
          <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="external-calendars">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-500"><Link2 size={13} /> Other calendars</div>
              <button onClick={() => setShowAddExternal((v) => !v)} className="h-7 px-2.5 rounded-lg border border-slate-200 hover:border-slate-900 text-[11.5px] font-semibold text-slate-700 inline-flex items-center gap-1" data-testid="add-external-toggle">
                <Plus size={12} /> Connect
              </button>
            </div>
            {externals.length === 0 && !showAddExternal && (
              <p className="text-[12px] text-slate-500">Connect Google, Outlook or Apple Calendar (read-only) so meetings there count as busy time.</p>
            )}
            <div className="space-y-2">
              {externals.map((c) => (
                <div key={c.id} className="border border-slate-200 rounded-lg p-2.5" data-testid={`external-${c.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm bg-violet-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{c.name}</div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {c.last_error
                          ? <span className="text-red-600">{c.last_error}</span>
                          : c.last_synced_at ? `${c.event_count} upcoming · synced ${new Date(c.last_synced_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Not synced yet"}
                      </div>
                    </div>
                    <button onClick={() => syncExternal(c.id)} disabled={syncingId === c.id} className="h-7 w-7 grid place-items-center rounded-lg border border-slate-200 text-slate-500 hover:border-slate-900 disabled:opacity-50" title="Sync now">
                      <RefreshCw size={12} className={syncingId === c.id ? "animate-spin" : ""} />
                    </button>
                    <button onClick={() => removeExternal(c)} className="h-7 w-7 grid place-items-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300" title="Disconnect">
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <label className="mt-2 flex items-center gap-2 text-[11.5px] text-slate-600 cursor-pointer select-none">
                    <input type="checkbox" checked={!!c.include_all_day} onChange={() => toggleAllDay(c)} className="h-3.5 w-3.5 accent-violet-600" />
                    Treat all-day events (holidays, leave) as busy
                  </label>
                </div>
              ))}
            </div>
            {showAddExternal && (
              <form onSubmit={addExternal} className="mt-3 space-y-2" data-testid="add-external-form">
                <input value={externalForm.name} onChange={(e) => setExternalForm({ ...externalForm, name: e.target.value })} placeholder="Name, e.g. Work Google Calendar" maxLength={80}
                  className="w-full h-9 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-slate-900 outline-none" data-testid="external-name" />
                <input value={externalForm.url} onChange={(e) => setExternalForm({ ...externalForm, url: e.target.value })} placeholder="Paste the iCal / ICS address (https:// or webcal://)"
                  className="w-full h-9 px-3 border border-slate-200 rounded-lg text-[13px] font-mono focus:border-slate-900 outline-none" data-testid="external-url" />
                <label className="flex items-center gap-2 text-[11.5px] text-slate-600 cursor-pointer select-none">
                  <input type="checkbox" checked={externalForm.include_all_day} onChange={(e) => setExternalForm({ ...externalForm, include_all_day: e.target.checked })} className="h-3.5 w-3.5 accent-violet-600" />
                  Treat all-day events as busy
                </label>
                <div className="flex gap-2">
                  <button type="submit" disabled={addingExternal} className="h-9 px-4 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                    {addingExternal ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Connect
                  </button>
                  <button type="button" onClick={() => setShowAddExternal(false)} className="h-9 px-3 border border-slate-200 rounded-lg text-[12.5px] text-slate-600">Cancel</button>
                </div>
                <div className="flex items-start gap-1.5 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-2.5 leading-relaxed">
                  <Info size={12} className="shrink-0 mt-0.5" />
                  <div>
                    <div><strong>Google Calendar:</strong> Settings → your calendar → Integrate calendar → <em>Secret address in iCal format</em>.</div>
                    <div className="mt-1"><strong>Outlook:</strong> Settings → Calendar → Shared calendars → Publish a calendar → copy the <em>ICS</em> link.</div>
                    <div className="mt-1"><strong>Apple iCloud:</strong> Share the calendar as Public and copy its webcal link.</div>
                    <div className="mt-1">Brainydo only reads; it refreshes every 30 minutes and never sees your other tasks' details beyond title and time.</div>
                  </div>
                </div>
              </form>
            )}
          </section>

          {/* Manual blocks */}
          <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="manual-blocks">
            <div className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-3"><CalendarPlus size={13} /> Busy blocks</div>
            {blocks.length === 0 ? (
              <p className="text-[12px] text-slate-500">Nothing blocked yet. Click a free spot on the grid, or use “Block time”, for lunch, site visits, leave, or a standing weekly meeting.</p>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-0.5">
                {blocks.map((b) => (
                  <button key={b.id} onClick={() => setBlockModal({ block: b })} className="w-full text-left border border-slate-200 rounded-lg px-2.5 py-2 hover:border-slate-900 transition" data-testid={`block-${b.id}`}>
                    <div className="text-[12.5px] font-semibold text-slate-800 truncate">{b.title}</div>
                    <div className="text-[11px] text-slate-400">
                      {b.kind === "weekly"
                        ? `Every ${(b.days || []).map((d) => DAY_NAMES[d]).join(", ")} · ${fmtHM(b.start_time)}–${fmtHM(b.end_time)}`
                        : `${new Date(b.start).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })} → ${new Date(b.end).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}`}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <p className="text-[11.5px] text-slate-400 px-1">Task deadlines come from the <Link to="/app/tasks" className="underline">Tasks</Link> page; each one takes a 10-minute reminder slot, the same length as its calendar invite; meetings take their real length.</p>
        </aside>
      </div>

      {focusTask && (
        <TaskDrawer
          task={focusTask}
          assignees={assignees}
          onClose={() => setFocusTask(null)}
          onUpdate={updateFocusTask}
          onDelete={deleteFocusTask}
          onSendInvite={sendFocusInvite}
          sendingInvite={sendingInvite}
        />
      )}

      {blockModal && (
        <BlockModal
          initial={blockModal}
          onSave={saveBlock}
          onDelete={blockModal.block?.id ? () => deleteBlock(blockModal.block.id) : null}
          onClose={() => setBlockModal(null)}
        />
      )}
    </div>
  );
}

function BlockModal({ initial, onSave, onDelete, onClose }) {
  const b = initial.block;
  const start = initial.start || (b?.start ? new Date(b.start) : new Date());
  const end = initial.end || (b?.end ? new Date(b.end) : new Date(start.getTime() + 60 * 60000));
  const [form, setForm] = useState({
    id: b?.id || null,
    title: b?.title || "",
    kind: b?.kind || "once",
    start: toLocalInput(start),
    end: toLocalInput(end),
    days: b?.days || [start.getDay() === 0 ? 6 : start.getDay() - 1],
    start_time: b?.start_time || `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
    end_time: b?.end_time || `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`,
    until: b?.until ? b.until.slice(0, 10) : "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave({ ...form, title: form.title.trim() || "Busy" }); } finally { setSaving(false); }
  };
  const inp = "w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-slate-900 outline-none";
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-[1px] grid place-items-center p-4 sw-fade-in" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="block-modal">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 sw-pop">
        <div className="px-5 pt-5 pb-3 flex items-start gap-3 border-b border-slate-100">
          <div className="flex-1">
            <h2 className="text-[15.5px] font-bold text-slate-900">{form.id ? "Edit busy block" : "Block time"}</h2>
            <p className="text-[12.5px] text-slate-500 mt-0.5">This time won't be suggested for task deadlines.</p>
          </div>
          <button type="button" onClick={onClose} className="h-7 w-7 grid place-items-center text-slate-400 hover:text-slate-900 rounded-lg hover:bg-slate-100"><X size={15} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What's happening? e.g. Site visit, Lunch, Leave" maxLength={120} className={inp} autoFocus data-testid="block-title" />
          <div className="grid grid-cols-2 gap-2">
            {[["once", "One-off"], ["weekly", "Every week"]].map(([k, l]) => (
              <button type="button" key={k} onClick={() => setForm({ ...form, kind: k })}
                className={"h-9 rounded-lg border text-[12.5px] font-semibold transition " + (form.kind === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400")} data-testid={`block-kind-${k}`}>
                {l}
              </button>
            ))}
          </div>
          {form.kind === "once" ? (
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[11.5px] font-semibold text-slate-600">From<input type="datetime-local" value={form.start} step={900} onChange={(e) => setForm({ ...form, start: e.target.value })} className={inp + " mt-1"} data-testid="block-start" /></label>
              <label className="text-[11.5px] font-semibold text-slate-600">To<input type="datetime-local" value={form.end} step={900} min={form.start} onChange={(e) => setForm({ ...form, end: e.target.value })} className={inp + " mt-1"} data-testid="block-end" /></label>
            </div>
          ) : (
            <>
              <div className="flex gap-1">
                {DAY_NAMES.map((n, i) => {
                  const on = form.days.includes(i);
                  return (
                    <button type="button" key={n} onClick={() => setForm({ ...form, days: on ? form.days.filter((d) => d !== i) : [...form.days, i].sort() })}
                      className={"flex-1 h-9 rounded-lg border text-[11.5px] font-semibold " + (on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200")}>
                      {n}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <label className="text-[11.5px] font-semibold text-slate-600">From<input type="time" step={900} value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={inp + " mt-1"} /></label>
                <label className="text-[11.5px] font-semibold text-slate-600">To<input type="time" step={900} value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={inp + " mt-1"} /></label>
                <label className="text-[11.5px] font-semibold text-slate-600">Until<input type="date" value={form.until} onChange={(e) => setForm({ ...form, until: e.target.value })} className={inp + " mt-1"} /></label>
              </div>
            </>
          )}
        </div>
        <div className="px-5 pb-5 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
          {onDelete ? <button type="button" onClick={onDelete} className="h-9 px-3 inline-flex items-center gap-1.5 text-[12.5px] text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={13} /> Remove</button> : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="h-9 px-3.5 border border-slate-200 hover:border-slate-400 rounded-lg text-[13px] font-medium text-slate-700">Cancel</button>
            <button type="submit" disabled={saving} className="h-9 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[13px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="block-save">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {form.id ? "Save" : "Block"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
